import React, { useState, useEffect } from "react";
import { useAuthContext } from "../hooks/useAuthContext";
import { getRoleDisplay } from "../utils/getRoleDisplay";
import Loading from "../utils/Loading";
import { Link, useParams } from "react-router-dom";
import { USERS_BASE_URL } from "../services/SignUpService";
import axios from "axios";

const MeProfile = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuthContext();
  const { id } = useParams();

  useEffect(() => {
    if (user) {
      setLoading(false);
    } else {
      setError({ message: "User data not found" });
      setLoading(false);
    }
  }, [user]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">{error.message}</div>
      </div>
    );
  }

  const deleteAccount = (e) => {
    e.preventDefault();
    if (
      window.confirm(
        "Are you sure you want to delete your account? This action cannot be undone."
      )
    ) {
      try {
        axios.delete(`${USERS_BASE_URL}/api/users/list/delete/${id}`);
      } catch (error) {
        console.log(error);
      }
    }
  };

  return (
    <div className="container py-5">
      <div className="row">
        <div className="col-lg-4">
          <div className="card shadow-sm mb-4">
            <div className="card-body text-center">
              <img
                src={(user && user.photo) || "https://i.pravatar.cc/150"}
                alt="Profile"
                className="rounded-circle mb-3 img-thumbnail"
                style={{ width: "150px", height: "150px", objectFit: "cover" }}
              />
              <h3 className="mb-1">
                {user.firstName} {user.lastName}
              </h3>
              <p className="text-muted mb-3">{user.email}</p>

              <div className="d-flex justify-content-center mb-3">
                <span
                  className={`badge ${
                    user?.isAdmin ? "bg-warning" : "bg-primary"
                  } me-2`}
                >
                  {getRoleDisplay(user)}
                </span>
                <span
                  className={`badge ${
                    user.isVerified ? "bg-success" : "bg-secondary"
                  }`}
                >
                  {user.isVerified ? "Verified" : "Not Verified"}
                </span>
              </div>

              {/* Display role badges if they exist */}
              {user.role && Array.isArray(user.role) && user.role.length > 0 && (
                <div className="d-flex justify-content-center flex-wrap gap-2 mb-3">
                  {user.role.map((role, index) => (
                    <span
                      key={index}
                      className={`badge ${
                        role === "Admin"
                          ? "bg-primary"
                          : role === "SuperAdmin"
                          ? "bg-danger"
                          : role === "Developer"
                          ? "bg-info"
                          : "bg-secondary"
                      }`}
                    >
                      {role}
                    </span>
                  ))}
                </div>
              )}

              <div className="d-grid gap-2">
                <Link
                  className="btn btn-primary"
                  title="Edit Account"
                  to={`/api/dashboard/user-profile/${user._id}`}
                >
                  <i className="fa fa-pencil me-2"></i> Edit Profile
                </Link>
                <button
                  className="btn btn-outline-danger"
                  disabled={user.isAdmin}
                  onClick={deleteAccount}
                >
                  <i className="fa fa-trash me-2"></i> Delete Account
                </button>
              </div>
            </div>
          </div>

          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title">Account Information</h5>
              <hr />
              <ul className="list-group list-group-flush">
                <li className="list-group-item d-flex justify-content-between align-items-center">
                  <span>Admin Status</span>
                  <span
                    className={`badge ${
                      user.isAdmin ? "bg-success" : "bg-secondary"
                    }`}
                  >
                    {user.isAdmin ? "Yes" : "No"}
                  </span>
                </li>
                <li className="list-group-item d-flex justify-content-between align-items-center">
                  <span>Member Since</span>
                  <span>
                    {user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString()
                      : "N/A"}
                  </span>
                </li>
                {user.role && Array.isArray(user.role) && user.role.length > 0 && (
                  <li className="list-group-item">
                    <span>Roles</span>
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      {user.role.map((role, index) => (
                        <span
                          key={index}
                          className={`badge ${
                            role === "Admin"
                              ? "bg-primary"
                              : role === "SuperAdmin"
                              ? "bg-danger"
                              : role === "Developer"
                              ? "bg-info"
                              : "bg-secondary"
                          }`}
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <h4 className="mb-0">Personal Information</h4>
            </div>
            <div className="card-body">
              <form>
                <div className="row mb-3">
                  <label
                    htmlFor="firstName"
                    className="col-sm-3 col-form-label"
                  >
                    First Name
                  </label>
                  <div className="col-sm-9">
                    <input
                      type="text"
                      className="form-control"
                      id="firstName"
                      value={user.firstName || ""}
                      disabled
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <label htmlFor="lastName" className="col-sm-3 col-form-label">
                    Last Name
                  </label>
                  <div className="col-sm-9">
                    <input
                      type="text"
                      className="form-control"
                      id="lastName"
                      value={user.lastName || ""}
                      disabled
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <label htmlFor="email" className="col-sm-3 col-form-label">
                    Email Address
                  </label>
                  <div className="col-sm-9">
                    <input
                      type="email"
                      className="form-control"
                      id="email"
                      value={user.email || ""}
                      disabled
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <label htmlFor="role" className="col-sm-3 col-form-label">
                    User Role
                  </label>
                  <div className="col-sm-9">
                    <input
                      type="text"
                      className={`form-control ${
                        user?.isAdmin ? "bg-warning text-white" : ""
                      }`}
                      id="role"
                      value={getRoleDisplay(user)}
                      disabled
                    />
                  </div>
                </div>

                {/* Display roles as badges if they exist */}
                {user.role && Array.isArray(user.role) && user.role.length > 0 && (
                  <div className="row mb-3">
                    <label className="col-sm-3 col-form-label">
                      Role Badges
                    </label>
                    <div className="col-sm-9">
                      <div className="d-flex flex-wrap gap-2">
                        {user.role.map((role, index) => (
                          <span
                            key={index}
                            className={`badge ${
                              role === "Admin" || role === "SuperAdmin"
                                ? "bg-warning"
                                : role === "Developer"
                                ? "bg-info"
                                : "bg-secondary"
                            }`}
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>

          <div className="card shadow-sm mt-4">
            <div className="card-header bg-white">
              <h4 className="mb-0">Account Security</h4>
            </div>
            <div className="card-body">
              <div className="alert alert-warning">
                <i className="fa fa-exclamation-triangle me-2"></i>
                For security reasons, some account changes may require
                verification.
              </div>
              <div className="d-grid gap-2">
                <button className="btn btn-outline-secondary">
                  <i className="fa fa-key me-2"></i> Change Password
                </button>
                <button className="btn btn-outline-secondary">
                  <i className="fa fa-envelope me-2"></i> Update Email
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeProfile;
